import React, { useMemo, useState, useEffect } from 'react';
import { useSidebar } from '../../contexts/SidebarContext';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { formatCurrencyINR } from '../../utils/currency';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useUserData } from '../../hooks/useUserData';
import { runMonteCarlo as runMC, summarize as summarizeMC } from '../../utils/monteCarlo';
import { buildApiUrl } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { useTour } from '../../contexts/TourContext';
import VideoModal from '../../components/ui/VideoModal';
import { getVideoForPage } from '../../config/videoConfig';
import DemoModeBanner from '../../components/demo/DemoModeBanner';

function boxMullerNormal(mean = 0, sd = 1) {
  let u = 0, v = 0;
  while (u === 0) u = Math.random();
  while (v === 0) v = Math.random();
  return Math.round(mean + sd * Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v));
}

function percentile(arr, p) {
  if (!arr.length) return 0;
  const sorted = [...arr].sort((a, b) => a - b);
  const idx = (sorted.length - 1) * p;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  const val = sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
  return Math.round(val);
}

function histogram(values, bins = 12) {
  if (!values.length) return { edges: [], counts: [] };
  const min = Math.min(...values);
  const max = Math.max(...values);
  const step = (max - min) / Math.max(1, bins);
  const edges = Array.from({ length: bins + 1 }, (_, i) => min + i * step);
  const counts = Array(bins).fill(0);
  values.forEach(v => {
    let b = step > 0 ? Math.floor((v - min) / step) : 0;
    if (b >= bins) b = bins - 1;
    if (b < 0) b = 0;
    counts[b]++;
  });
  return { edges, counts };
}

function runMonteCarlo({ startCorpus, income, expense, meanReturn, sdReturn, meanInflation, sdInflation, years, runs }) {
  const allRuns = [];
  for (let r = 0; r < runs; r++) {
    let corpus = startCorpus;
    const path = [];
    for (let y = 0; y < years; y++) {
      const ret = boxMullerNormal(meanReturn, sdReturn);
      const inf = boxMullerNormal(meanInflation, sdInflation);
      corpus = Math.max(0, Math.round(corpus * (1 + ret / 100 - inf / 100) + (income - expense)));
      path.push(corpus);
    }
    allRuns.push(path);
  }
  return allRuns;
}

function summarize(allRuns, targetCorpus) {
  const years = allRuns[0]?.length || 0;
  const finals = allRuns.map(r => r[years - 1] || 0);
  const success = finals.filter(v => v >= targetCorpus).length / Math.max(1, allRuns.length);
  const avg = Array.from({ length: years }, (_, i) => {
    const s = allRuns.reduce((acc, r) => acc + (r[i] || 0), 0);
    return Math.round(s / Math.max(1, allRuns.length));
  });
  const p5 = percentile(finals, 0.05);
  const p50 = percentile(finals, 0.50);
  const p95 = percentile(finals, 0.95);
  const hist = histogram(finals, 12);
  return { p5, p50, p95, success, avg, hist };
}

const MonteCarloPage = () => {
  const baseScenarios = {
    Base: { meanReturn: 8, sdReturn: 12, meanInflation: 5, sdInflation: 1.5, color: '#1E40AF' },
    'High Inflation': { meanReturn: 8, sdReturn: 12, meanInflation: 7, sdInflation: 2.0, color: '#F59E0B' },
    'Low Return': { meanReturn: 6, sdReturn: 10, meanInflation: 5, sdInflation: 1.5, color: '#EF4444' },
    Custom: null,
  };

  const { userData } = useUserData();
  const { getAuthHeaders } = useAuth();
  const { startMonteCarloTour, autoStartTour } = useTour();
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const defaultYears = userData?.setup?.projectionHorizon || 20;
  const defaultStartCorpus = Math.max(0, (userData?.engineMetrics?.[0]?.corpusAvailable || 0));
  const defaultIncome = Math.round((userData?.incomeExpansion || []).reduce((s, r) => s + (r.annualValues?.[0] || 0), 0));
  const defaultExpense = Math.round((userData?.expenseExpansion || []).reduce((s, r) => s + (r.annualValues?.[0] || 0), 0));

  const [scenario, setScenario] = useState('Base');
  const [customParams, setCustomParams] = useState(null);
  const [startCorpus, setStartCorpus] = useState(defaultStartCorpus || 3000000);
  const [income, setIncome] = useState(defaultIncome || 1500000);
  const [expense, setExpense] = useState(defaultExpense || 1200000);
  const [target, setTarget] = useState(5000000);
  const [years, setYears] = useState(defaultYears);
  const [runs, setRuns] = useState(1000);

  const videoConfig = getVideoForPage('monte-carlo');

  // Auto-start tour for first-time visitors
  useEffect(() => {
    if (userData) {
      autoStartTour('monte-carlo', startMonteCarloTour);
    }
  }, [userData, autoStartTour, startMonteCarloTour]);

  const currentYear = new Date().getFullYear();
  const params = (scenario === 'Custom' && customParams) ? customParams : baseScenarios[scenario];

  const { allRuns, stats } = useMemo(() => {
    const t0 = performance.now();
    const r = runMC({
      startCorpus,
      income,
      expense,
      meanReturn: params.meanReturn / 100,
      sdReturn: params.sdReturn / 100,
      meanInflation: params.meanInflation / 100,
      sdInflation: params.sdInflation / 100,
      years,
      runs
    });
    const s = summarizeMC(r, target);
    const t1 = performance.now();
    // Log runtime once per settings change
    // eslint-disable-next-line no-console
    console.log(`[MonteCarlo] runs=${runs}, years=${years} -> ${Math.round(t1 - t0)}ms`);
    return { allRuns: r, stats: s };
  }, [startCorpus, income, expense, params, years, runs, target]);

  // Hydrate inputs from stored montecarlo.settings (if present)
  useEffect(() => {
    const s = userData?.montecarlo?.settings;
    if (!s) return;
    if (typeof s.years === 'number') setYears(s.years);
    if (typeof s.numSimulations === 'number') setRuns(s.numSimulations);
    if (typeof s.startCorpus === 'number') setStartCorpus(s.startCorpus);
    if (typeof s.annualIncome === 'number') setIncome(s.annualIncome);
    if (typeof s.annualExpense === 'number') setExpense(s.annualExpense);
    if (typeof s.targetFinalCorpus === 'number') setTarget(s.targetFinalCorpus);
    const ret = s.returns || {};
    const inf = s.inflation || {};
    if (
      (typeof ret.mean === 'number' && !Number.isNaN(ret.mean)) ||
      (typeof ret.vol === 'number' && !Number.isNaN(ret.vol)) ||
      (typeof inf.mean === 'number' && !Number.isNaN(inf.mean)) ||
      (typeof inf.vol === 'number' && !Number.isNaN(inf.vol))
    ) {
      setCustomParams({
        meanReturn: typeof ret.mean === 'number' ? ret.mean : baseScenarios.Base.meanReturn,
        sdReturn: typeof ret.vol === 'number' ? ret.vol : baseScenarios.Base.sdReturn,
        meanInflation: typeof inf.mean === 'number' ? inf.mean : baseScenarios.Base.meanInflation,
        sdInflation: typeof inf.vol === 'number' ? inf.vol : baseScenarios.Base.sdInflation,
        color: '#1E40AF'
      });
      setScenario('Custom');
    }
  }, [userData?.montecarlo]);

  const labels = Array.from({ length: years }, (_, i) => currentYear + i);
  const fanDataset = allRuns.slice(0, 5).map((r, i) => ({ label: `Run ${i + 1}`, data: r }));

  const histLabels = stats.hist.edges.slice(1).map((edge, i) => {
    const lo = stats.hist.edges[i] || 0;
    const hi = edge || 0;
    // Use compact formatting to avoid long labels and reduce clipping
    return `${formatCurrencyINR(lo, { showDecimals: false, showSymbol: false, compact: true })}–${formatCurrencyINR(hi, { showDecimals: false, showSymbol: false, compact: true })}`;
  });

  const { sidebarExpanded, toggleSidebar } = useSidebar();

  const formatAxisINRCompact = (value) => {
    return formatCurrencyINR(value, { showDecimals: false, compact: true, showSymbol: false });
  };

  const saveSettings = async () => {
    const settings = {
      numSimulations: runs,
      confidenceLevels: [5, 25, 50, 75, 95],
      rebalancingPolicy: 'annual',
      years,
      startCorpus,
      annualIncome: income,
      annualExpense: expense,
      targetFinalCorpus: target,
      inflation: { mean: params.meanInflation, vol: params.sdInflation },
      returns: { mean: params.meanReturn, vol: params.sdReturn }
    };
    const payload = { montecarlo: { version: 1, settings } };
    try {
      const res = await fetch(buildApiUrl('/auth/user-data/update'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload)
      });
      // eslint-disable-next-line no-console
      console.log('[MonteCarlo] Saved settings:', settings, 'status=', res.status);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[MonteCarlo] Save settings failed', e);
    }
  };

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <Sidebar />
      <Header sidebarExpanded={sidebarExpanded} onSidebarToggle={toggleSidebar} />
      <DemoModeBanner />
      <main
        className={`
          transition-all duration-300 ease-spring pt-16 pb-20 lg:pb-8
          ${sidebarExpanded ? 'lg:ml-64' : 'lg:ml-16'}
        `}
      >
      <div className="p-4 sm:p-6">
        {/* Breadcrumb Navigation */}
        <div className="flex items-center space-x-2 text-sm text-muted-foreground mb-4">
          <Icon name="Home" size={16} />
          <span>Home</span>
          <Icon name="ChevronRight" size={16} />
          <Icon name="BarChart3" size={16} />
          <span>Monte Carlo</span>
        </div>

        {/* Header with Title and Action Buttons */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground mb-1">Monte Carlo Simulation</h1>
            <p className="text-muted-foreground">Simulate wealth trajectory against market and inflation variability</p>
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="outline" iconName="Video" iconPosition="left" onClick={() => setIsVideoOpen(true)} className="hidden sm:inline-flex">
              Learn
            </Button>
            <Button variant="outline" iconName="HelpCircle" iconPosition="left" onClick={startMonteCarloTour} className="hidden sm:inline-flex">
              Tour
            </Button>
          </div>
        </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Inputs */}
        <div className="bg-card p-5 rounded-lg border border-border lg:col-span-1" data-tour="monte-carlo-parameters">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-foreground">
              <span title="Premium Feature - Advanced Monte Carlo simulation parameters" className="cursor-help">🚀</span> Parameters
            </h2>
            <Button variant="outline" iconName="Save" iconPosition="left" onClick={saveSettings} data-tour="monte-carlo-save">
              Save
            </Button>
          </div>
          <div className="space-y-4">
            <div data-tour="monte-carlo-scenario">
              <label className="block text-sm font-medium text-foreground mb-1">Scenario</label>
              <select value={scenario} onChange={(e) => setScenario(e.target.value)} className="w-full rounded-md border border-border p-2 bg-background">
                {Object.keys(baseScenarios).map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <Input label="Starting Corpus (₹)" type="number" value={startCorpus} onChange={(e) => setStartCorpus(Number(e.target.value))} />
            <Input label="Annual Income (₹)" type="number" value={income} onChange={(e) => setIncome(Number(e.target.value))} />
            <Input label="Annual Expense (₹)" type="number" value={expense} onChange={(e) => setExpense(Number(e.target.value))} />
            <Input label="Target Final Corpus (₹)" type="number" value={target} onChange={(e) => setTarget(Number(e.target.value))} />
            <div className="grid grid-cols-2 gap-3">
              <Input label="Years" type="number" value={years} onChange={(e) => setYears(parseInt(e.target.value) || 1)} />
              <Input label="Runs" type="number" value={runs} onChange={(e) => setRuns(parseInt(e.target.value) || 100)} />
            </div>
            <div className="text-sm text-muted-foreground space-y-1">
              <div>Mean Return: <span className="font-medium" style={{ color: params.color }}>{params.meanReturn}%</span></div>
              <div>Return Volatility: <span className="font-medium">{params.sdReturn}%</span></div>
              <div>Mean Inflation: <span className="font-medium">{params.meanInflation}%</span></div>
              <div>Inflation Vol.: <span className="font-medium">{params.sdInflation}%</span></div>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="lg:col-span-3 space-y-6">
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-tour="monte-carlo-kpis">
            <div className="bg-card rounded-lg border border-border p-4 text-center">
              <div className="text-sm text-muted-foreground">Success Prob.</div>
              <div className="text-2xl font-bold text-foreground">{Math.round(stats.success * 100)}%</div>
            </div>
            <div className="bg-card rounded-lg border border-border p-4 text-center">
              <div className="text-sm text-muted-foreground">Median Final Corpus</div>
              <div className="text-2xl font-bold text-foreground">{formatCurrencyINR(stats.p50)}</div>
            </div>
            <div className="bg-card rounded-lg border border-border p-4 text-center">
              <div className="text-sm text-muted-foreground">P5 Final Corpus</div>
              <div className="text-2xl font-bold text-foreground">{formatCurrencyINR(stats.p5)}</div>
            </div>
            <div className="bg-card rounded-lg border border-border p-4 text-center">
              <div className="text-sm text-muted-foreground">P95 Final Corpus</div>
              <div className="text-2xl font-bold text-foreground">{formatCurrencyINR(stats.p95)}</div>
            </div>
          </div>

          {/* Fan chart (sample runs) */}
          <div className="bg-card rounded-lg border border-border p-4" data-tour="monte-carlo-trajectories">
            <h3 className="text-lg font-semibold text-foreground mb-3">Sample Run Trajectories</h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={labels.map((year, i) => ({ year, ...Object.fromEntries(fanDataset.map((d, idx) => [idx, d.data[i] || 0])) }))} margin={{ left: 28, right: 8, top: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="year" stroke="#6B7280" fontSize={12} />
                  <YAxis stroke="#6B7280" fontSize={12} width={56} tickFormatter={formatAxisINRCompact} />
                  <Tooltip formatter={(v) => formatCurrencyINR(Number(v) || 0)} labelFormatter={(l) => `${l}`} />
                  {fanDataset.map((d, idx) => (
                    <Line key={idx} type="monotone" dataKey={String(idx)} stroke={params.color} strokeWidth={idx === 0 ? 2 : 1} dot={false} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Average corpus over time */}
          <div className="bg-card rounded-lg border border-border p-4" data-tour="monte-carlo-average">
            <h3 className="text-lg font-semibold text-foreground mb-3">Average Corpus Over Time</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={labels.map((year, i) => ({ year, avg: stats.avg[i] || 0 }))} margin={{ left: 28, right: 8, top: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="year" stroke="#6B7280" fontSize={12} />
                  <YAxis stroke="#6B7280" fontSize={12} width={56} tickFormatter={formatAxisINRCompact} />
                  <Tooltip formatter={(v) => formatCurrencyINR(Number(v) || 0)} labelFormatter={(l) => `${l}`} />
                  <Line type="monotone" dataKey="avg" stroke={params.color} strokeWidth={3} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Histogram */}
          <div className="bg-card rounded-lg border border-border p-4" data-tour="monte-carlo-histogram">
            <h3 className="text-lg font-semibold text-foreground mb-3">Final Corpus Distribution</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.hist.counts.map((c, i) => ({ bin: histLabels[i] || '', count: c }))} margin={{ bottom: 90, left: 8, right: 8, top: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="bin" stroke="#6B7280" fontSize={11} angle={-45} textAnchor="end" height={80} interval={0} />
                  <YAxis stroke="#6B7280" fontSize={12} />
                  <Tooltip labelFormatter={(l) => l} formatter={(v) => `${v} runs`} />
                  <Bar dataKey="count" fill="#95A5A6" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
      </div>
      </main>
      
      {/* Video Modal */}
      <VideoModal
        isOpen={isVideoOpen}
        onClose={() => setIsVideoOpen(false)}
        videoUrl={videoConfig?.url}
        title={videoConfig?.title}
      />
    </div>
  );
};

export default MonteCarloPage;


